/*
 * aura-collection.js — keep a collection page where the visitor left it.
 *
 * THE PROBLEM (diagnosed 24 Aug): this theme's pagination is not links. It is a
 * "View More" button that fetches the next page and appends `li.grid__item` into
 * `ul#product-grid` WITHOUT touching the URL. So after loading four pages and
 * clicking a product, Back returns to a URL that never changed — page one — and
 * every product loaded after the first batch is gone. Browser scroll restoration
 * cannot help, because the elements it would scroll to no longer exist.
 *
 * THE FIX: remember how many pages were loaded and where the visitor was, then on
 * return re-fetch those pages, re-append them, and scroll back.
 *
 * Only restores when arriving FROM a product page, so navigating to a collection
 * fresh from the nav still starts at the top with one page, as expected.
 */
(function () {
  var GRID = 'ul#product-grid';
  var TTL = 30 * 60 * 1000; // half an hour

  function grid() { return document.querySelector(GRID); }
  function btn() { return document.getElementById('load-more-btn'); }

  // key on path + any filter/sort query, minus page, so filtered views are distinct
  function key() {
    var p = new URLSearchParams(location.search);
    p.delete('page');
    var q = p.toString();
    return 'aura:coll:' + location.pathname + (q ? '?' + q : '');
  }

  function pageSize() {
    var b = btn();
    var n = b && parseInt(b.getAttribute('data-page-size'), 10);
    return n && n > 0 ? n : 12;
  }

  function pagesLoaded() {
    var g = grid();
    if (!g) return 1;
    var count = g.querySelectorAll(':scope > li.grid__item').length;
    return Math.max(1, Math.ceil(count / pageSize()));
  }

  // `href` is the product the visitor clicked. Storing it is what makes Back land
  // on that exact product rather than near a remembered pixel offset: after the
  // pages are re-appended we scroll the CARD into view, which survives images
  // decoding, column-count changes and any layout edit made since.
  function save(href) {
    var g = grid();
    if (!g) return;
    try {
      var prev = read() || {};
      sessionStorage.setItem(key(), JSON.stringify({
        pages: pagesLoaded(),
        y: window.pageYOffset || document.documentElement.scrollTop || 0,
        // a save from pagehide has no href; keep the one the click recorded
        p: href || prev.p || null,
        t: Date.now()
      }));
    } catch (e) { /* private mode / quota — restoration is a nicety, never a blocker */ }
  }

  // Scroll the remembered product into view. Falls back to the stored offset when
  // the card cannot be found (product unpublished, filter changed under us).
  function land(state) {
    var card = null;
    if (state.p && typeof state.p === 'string') {
      var path = state.p.split('?')[0];
      card = document.querySelector(GRID + ' a[href^="' + path + '"]');
      if (!card) {
        var handle = path.split('/').filter(Boolean).pop();
        if (handle) card = document.querySelector(GRID + ' a[href*="/' + handle + '"]');
      }
    }
    if (card) {
      card.scrollIntoView({ block: 'center' });
      card.closest('li') && card.closest('li').setAttribute('data-aura-returned', 'true');
    } else {
      window.scrollTo(0, state.y);
    }
  }

  function read() {
    try {
      var raw = sessionStorage.getItem(key());
      if (!raw) return null;
      var s = JSON.parse(raw);
      if (!s || Date.now() - s.t > TTL) return null;
      return s;
    } catch (e) { return null; }
  }

  function pageUrl(n) {
    var p = new URLSearchParams(location.search);
    p.set('page', n);
    return location.pathname + '?' + p.toString();
  }

  // Append one page's items. Returns the parsed doc so the caller can read the
  // fresh load-more button state from the LAST page it fetched.
  function fetchPage(n) {
    return fetch(pageUrl(n), { credentials: 'same-origin' })
      .then(function (r) { return r.text(); })
      .then(function (html) {
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var g = grid();
        var items = doc.querySelectorAll(GRID + ' > li.grid__item');
        if (g && items.length) {
          items.forEach(function (li) { g.appendChild(document.importNode(li, true)); });
        }
        return doc;
      });
  }

  // Hand the theme's own button the state it would have had, so the next click
  // continues from the right page instead of re-fetching page two.
  function syncButton(doc) {
    var b = btn();
    var fresh = doc && doc.getElementById('load-more-btn');
    if (!b) return;
    if (!fresh) {
      var wrap = document.querySelector('.load-more-buttons');
      if (wrap) wrap.style.display = 'none';
      return;
    }
    ['data-next-url', 'data-current-page', 'data-total-pages', 'data-total-items', 'data-page-size']
      .forEach(function (a) {
        var v = fresh.getAttribute(a);
        if (v !== null) b.setAttribute(a, v);
      });
    var bar = document.getElementById('load-more-bar');
    var count = document.getElementById('pagination-count');
    var freshBar = doc.getElementById('load-more-bar');
    var freshCount = doc.getElementById('pagination-count');
    if (bar && freshBar) bar.style.width = freshBar.style.width;
    if (count && freshCount) count.textContent = freshCount.textContent;
  }

  function restore(state) {
    var target = Math.min(state.pages, 50); // guard against a silly stored value
    if (target <= 1) { land(state); return; }

    var note = document.createElement('p');
    note.className = 'aura-restore-note';
    note.textContent = 'Returning you to where you were…';
    var g = grid();
    if (g && g.parentNode) g.parentNode.insertBefore(note, g.nextSibling);

    // sequential on purpose: order matters, and parallel appends would interleave
    var chain = Promise.resolve(null);
    for (var n = 2; n <= target; n++) {
      (function (page) {
        chain = chain.then(function () { return fetchPage(page); });
      })(n);
    }
    chain
      .then(function (lastDoc) { syncButton(lastDoc); })
      .catch(function () { /* leave the page usable at one batch */ })
      .then(function () {
        note.remove();
        land(state);
        // images shifting as they decode can move the anchor; settle twice more
        setTimeout(function () { land(state); }, 250);
        setTimeout(function () { land(state); }, 900);
      });
  }

  function init() {
    if (!grid()) return;

    // we manage the position ourselves; the browser's guess is wrong here because
    // the page it would restore against has fewer products than the one we left
    if ('scrollRestoration' in history) history.scrollRestoration = 'manual';

    var state = read();

    // `document.referrer` is the WRONG test. On a Back navigation it reports the
    // history entry's original referrer (measured: "https://shopauraluxury.com/"),
    // not the product just left, so the restore never fired. The reliable signal is
    // the navigation type. Referrer is kept as a secondary route for browsers that
    // do not expose the timing entry.
    var nav = (performance.getEntriesByType && performance.getEntriesByType('navigation')[0]) || null;
    var isBack = nav ? nav.type === 'back_forward'
                     : (performance.navigation && performance.navigation.type === 2);
    var cameFromProduct = /\/products\//.test(document.referrer || '');
    if (state && (isBack || cameFromProduct)) restore(state);

    // save on the way out
    document.addEventListener('click', function (e) {
      var a = e.target.closest && e.target.closest('a[href*="/products/"]');
      if (a) save(a.getAttribute('href'));
    }, true);
    // NOT `addEventListener('pagehide', save)` - that hands the PageTransitionEvent
    // to save()'s first parameter, which stored `{"isTrusted":true}` as the product.
    window.addEventListener('pagehide', function () { save(); });
    document.addEventListener('visibilitychange', function () {
      if (document.visibilityState === 'hidden') save();
    });
  }

  if (document.readyState !== 'loading') init();
  else document.addEventListener('DOMContentLoaded', init);
})();

/* ===========================================================================
   IN-STOCK ONLY toggle - added 2 Sep 2026.

   Ring Concierge's filter bar ends with `IN-STOCK ONLY [switch]` rather than an
   Availability dropdown (read live 2 Sep 2026 on /collections/fine-jewelry).
   Aura's Availability filter went live in Search & Discovery the same day, so
   this promotes it out of its dropdown and into a switch.

   It does not invent a filter. It drives the theme's own
   `input[name="filter.v.availability"][value="1"]` checkbox and then submits the
   real facet form, so history, the URL and the "clear all" link keep working.

   Re-runs on facet re-render: Dawn replaces the whole facets container after a
   filter is applied, which destroys anything injected here. A MutationObserver
   on the container is the only reliable hook - there is no public event.
   =========================================================================== */
(function () {
  var BAR = '.facets-container-horizontal';
  var IN_STOCK = 'input[name="filter.v.availability"][value="1"]';

  function build(bar) {
    if (!bar || bar.querySelector('.aura-instock')) return;

    // Only offer the switch when the store actually exposes the filter.
    var source = bar.querySelector(IN_STOCK);
    if (!source) return;

    var wrap = document.createElement('label');
    wrap.className = 'aura-instock';
    wrap.innerHTML =
      '<span class="aura-instock__label">In stock only</span>' +
      '<span class="aura-instock__track"><span class="aura-instock__knob"></span></span>';

    var box = document.createElement('input');
    box.type = 'checkbox';
    box.className = 'aura-instock__input';
    box.checked = source.checked;
    wrap.insertBefore(box, wrap.firstChild);

    box.addEventListener('change', function () {
      // Re-query: the node this closure captured may already have been replaced.
      var live = document.querySelector(BAR + ' ' + IN_STOCK);
      if (!live) return;
      live.checked = box.checked;
      // Dispatch on the input so the theme's own facet handler picks it up and
      // does the fetch, URL update and history push itself.
      live.dispatchEvent(new Event('input', { bubbles: true }));
      live.dispatchEvent(new Event('change', { bubbles: true }));
      var form = live.closest('form');
      if (form && typeof form.requestSubmit === 'function') {
        try { form.requestSubmit(); } catch (e) { /* handler already ran */ }
      }
    });

    // Mount inside .facets__wrapper (the flex row that holds the dropdowns), not
    // on the bar itself. The bar is a CSS grid, so appending there put the switch
    // on its own second row instead of inline at the end of the filters.
    // Mount into .facets__form, NOT .facets__wrapper. The wrapper only holds the
    // filter dropdowns; sort is its sibling. RC's order is filters, SORT, then the
    // switch, so the switch has to live at the same level as sort to sit after it.
    var host = bar.querySelector('.facets__form') || bar;
    host.appendChild(wrap);
    bar.classList.add('aura-has-instock');
  }

  function init() {
    var bar = document.querySelector(BAR);
    if (!bar) return;
    build(bar);

    // The container is replaced wholesale on every filter change.
    var host = bar.parentElement || document.body;
    new MutationObserver(function () {
      var fresh = document.querySelector(BAR);
      if (fresh) build(fresh);
    }).observe(host, { childList: true, subtree: true });
  }

  if (document.readyState !== 'loading') init();
  else document.addEventListener('DOMContentLoaded', init);
})();

/* ===========================================================================
   PRICE BANDS - added 2 Sep 2026.

   Brad: "Price should be in the same blocks as RC does it here."

   Shopify's price filter is a RANGE (filter.v.price.gte / .lte), not a list, so
   discrete bands cannot come from Search & Discovery. Ring Concierge draw their
   own: their PRICE checkboxes carry NO `name` attribute at all, which is the tell
   that they are custom controls mapped onto gte/lte. This does the same.

   Multi-select maps to a single range by taking min(low) and max(high) across the
   ticked bands, because gte/lte can only express one interval. Ticking two
   adjacent bands therefore behaves exactly as expected; ticking two separated ones
   widens to span them, which is the only honest thing a single range can do.

   Band definitions come from the Aura filter policy section, not from here, so the
   amounts stay editable in the theme editor.
   =========================================================================== */
(function () {
  var UNBOUNDED = 0; // Liquid renders a blank max as 0

  function bands() {
    var el = document.getElementById('aura-price-bands');
    if (!el) return null;
    try {
      var parsed = JSON.parse(el.textContent);
      return parsed && parsed.length ? parsed : null;
    } catch (e) {
      return null;
    }
  }

  function money(n) {
    return '$' + Number(n).toLocaleString('en-US');
  }

  // Read the applied range from the URL, not from the inputs. After the theme
  // re-renders the facets the new price inputs can still be empty while the URL
  // already carries the range, which left the ticked band looking unticked.
  function applied() {
    var q = new URLSearchParams(location.search);
    return {
      lo: parseFloat(q.get('filter.v.price.gte')) || 0,
      hi: parseFloat(q.get('filter.v.price.lte')) || 0
    };
  }

  function reflect(list, defs) {
    var cur = applied();
    var hiEff = cur.hi === 0 ? Infinity : cur.hi;
    [].forEach.call(list.querySelectorAll('.aura-bands__input'), function (box, i) {
      var b = defs[i];
      if (!b) return;
      var hi = b.max === UNBOUNDED ? Infinity : b.max;
      box.checked = (cur.lo || cur.hi) ? (b.min >= cur.lo && hi <= hiEff) : false;
    });
  }

  function build(panel, defs) {
    if (!panel) return;

    // Already built: refresh the ticks rather than bailing, so a re-render that
    // reuses the panel still shows the applied band.
    var existing = panel.querySelector('.aura-bands');
    if (existing) { reflect(existing, defs); return; }

    var gte = panel.querySelector('input[name="filter.v.price.gte"]');
    var lte = panel.querySelector('input[name="filter.v.price.lte"]');
    if (!gte || !lte) return;

    var list = document.createElement('ul');
    list.className = 'aura-bands';
    list.setAttribute('role', 'list');

    defs.forEach(function (b, i) {
      var li = document.createElement('li');
      li.className = 'aura-bands__item';

      var id = 'aura-band-' + Math.random().toString(36).slice(2, 8) + '-' + i;
      var box = document.createElement('input');
      box.type = 'checkbox';
      box.className = 'aura-bands__input';
      box.id = id;
      box.value = i;

      var label = document.createElement('label');
      label.className = 'aura-bands__label';
      label.setAttribute('for', id);
      label.textContent =
        b.label ||
        (b.max === UNBOUNDED ? 'Above ' + money(b.min)
          : b.min === 0 ? 'Under ' + money(b.max)
            : money(b.min) + ' - ' + money(b.max));

      li.appendChild(box);
      li.appendChild(label);
      list.appendChild(li);
    });

    list.addEventListener('change', function () {
      var checked = [].slice.call(list.querySelectorAll('.aura-bands__input:checked'))
        .map(function (i) { return defs[parseInt(i.value, 10)]; });

      var lo = '', hi = '';
      if (checked.length) {
        lo = Math.min.apply(null, checked.map(function (b) { return b.min; }));
        var anyOpen = checked.some(function (b) { return b.max === UNBOUNDED; });
        hi = anyOpen ? '' : Math.max.apply(null, checked.map(function (b) { return b.max; }));
        if (lo === 0) lo = '';
      }

      // Re-query: this panel may have been replaced since the listener was bound.
      var liveGte = document.querySelector('.facets-container-horizontal input[name="filter.v.price.gte"]');
      var liveLte = document.querySelector('.facets-container-horizontal input[name="filter.v.price.lte"]');
      if (!liveGte || !liveLte) return;

      liveGte.value = lo === '' ? '' : String(lo);
      liveLte.value = hi === '' ? '' : String(hi);

      [liveGte, liveLte].forEach(function (input) {
        input.dispatchEvent(new Event('input', { bubbles: true }));
        input.dispatchEvent(new Event('change', { bubbles: true }));
      });

      var form = liveGte.closest('form');
      if (form && typeof form.requestSubmit === 'function') {
        try { form.requestSubmit(); } catch (e) { /* the theme handler already ran */ }
      }
    });

    // Keep the native inputs in the DOM (the theme and the form both read them),
    // just out of sight. Hiding the wrapper rather than removing it means a JS
    // failure anywhere above leaves the original range UI usable.
    panel.classList.add('aura-bands-on');
    panel.insertBefore(list, panel.firstChild);
    reflect(list, defs);
  }

  function init() {
    var defs = bands();
    if (!defs) return;

    function pass() {
      var panels = document.querySelectorAll(
        '.facets-container-horizontal .facets__display, .facets-container-horizontal .mobile-facets__submenu'
      );
      [].forEach.call(panels, function (p) { build(p, defs); });
    }

    pass();

    var bar = document.querySelector('.facets-container-horizontal');
    var host = (bar && bar.parentElement) || document.body;
    new MutationObserver(pass).observe(host, { childList: true, subtree: true });
  }

  if (document.readyState !== 'loading') init();
  else document.addEventListener('DOMContentLoaded', init);
})();
